import { useEffect, useState } from "react";

/**
 * Utility hook for managing the outputs of standard canvas components like `TextAreaAlpha` and
 * `InputTextAlpha`.
 *
 * These components can be configured to either produce a value any time the user changes the value,
 * or when the user submits the value. This hook manages the bookkeeping particularly related to
 * that.
 */
export const useInputValue = (
	pushCondition: "onChange" | "onSubmit",
	defaultValue: string,
	externalValue: string | undefined
) => {
	let value = "";
	if (externalValue !== undefined) {
		value = externalValue;
	} else if (defaultValue !== undefined) {
		value = defaultValue;
	}

	// Bookkeeping for the text input's internal value.
	const [internalValue, setInternalValue] = useState(value);
	useEffect(() => {
		setInternalValue(value);
	}, [value]);

	//
	// Keep track of whether submit button can be pressed, i.e.,
	// whether the text input field is "dirty.""
	//

	const [dirty, setDirty] = useState(false);
	useEffect(() => {
		setDirty(pushValue !== internalValue);
	}, [internalValue]);

	// The value to "push" to other cells.
	const [pushValue, setPushValue] = useState(internalValue);

	useEffect(() => {
		if (pushCondition === "onChange") {
			setPushValue(internalValue);
		}
	}, [internalValue]);

	return {
		setInternalValue,
		pushValue,
		onPush: () => {
			setDirty(false);
			setPushValue(internalValue);
		},
		dirty,
	};
};
